import React, { useState } from 'react';
import { Modal, Button, Input, Toast } from './common';
import { useAuth } from '../context/AuthContext';
import authService from '../services/auth.service';
import './ChangePasswordModal.css';

interface ChangePasswordModalProps {
  isOpen: boolean;
  isFirstTime?: boolean; // Si es true, no se requiere contraseña actual
  onClose?: () => void;
  onSuccess?: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  isFirstTime = false,
  onClose,
  onSuccess
}) => {
  const { updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success' as 'success' | 'error'
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!isFirstTime && !formData.currentPassword) {
      newErrors.currentPassword = 'La contraseña actual es requerida';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'La nueva contraseña es requerida';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirma tu nueva contraseña';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setIsLoading(true);

      const result = await authService.changePassword({
        currentPassword: isFirstTime ? undefined : formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (result.success) {
        // Actualizar el estado del usuario
        updateUser({ must_change_password: false });

        setToast({
          isVisible: true,
          message: 'Contraseña actualizada exitosamente',
          type: 'success'
        });

        // Limpiar formulario
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });

        // Llamar callback de éxito
        setTimeout(() => {
          if (onSuccess) onSuccess();
        }, 1500);
      }
    } catch (error: any) {
      setToast({
        isVisible: true,
        message: error.message || 'Error al cambiar la contraseña',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // No permitir cerrar si es primera vez
  const handleClose = () => {
    if (!isFirstTime && onClose) {
      onClose();
    }
  };

  return (
    <>
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={isFirstTime ? 'Actualiza tu Contraseña' : 'Cambiar Contraseña'}
        size="small"
        showCloseButton={!isFirstTime}
        preventClose={isFirstTime}
      >
        <div className="change-password-modal">
          {isFirstTime && (
            <div className="first-time-banner">
              <div className="banner-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M12 8v4M12 16h.01" />
                </svg>
              </div>
              <div className="banner-content">
                <h4>Bienvenido al Portal de Estados de Cuenta</h4>
                <p>Por seguridad, debes establecer una nueva contraseña antes de continuar.</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="password-form">
            {!isFirstTime && (
              <Input
                label="Contraseña actual"
                type="password"
                value={formData.currentPassword}
                onChange={handleChange('currentPassword')}
                error={errors.currentPassword}
                placeholder="Ingresa tu contraseña actual"
                leftIcon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                }
              />
            )}

            <Input
              label="Nueva contraseña"
              type="password"
              value={formData.newPassword}
              onChange={handleChange('newPassword')}
              error={errors.newPassword}
              placeholder="Minimo 6 caracteres"
              helperText="Usa una combinación de letras, números y simbolos"
              leftIcon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              }
            />

            <Input
              label="Confirmar nueva contraseña"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              error={errors.confirmPassword}
              placeholder="Repite la nueva contraseña"
              leftIcon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              }
            />

            <div className="password-requirements">
              <h5>Requisitos de la contraseña:</h5>
              <ul>
                <li className={formData.newPassword.length >= 6 ? 'valid' : ''}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {formData.newPassword.length >= 6 ? (
                      <polyline points="20 6 9 17 4 12" />
                    ) : (
                      <circle cx="12" cy="12" r="10" />
                    )}
                  </svg>
                  Minimo 6 caracteres
                </li>
                <li className={formData.newPassword === formData.confirmPassword && formData.confirmPassword !== '' ? 'valid' : ''}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {formData.newPassword === formData.confirmPassword && formData.confirmPassword !== '' ? (
                      <polyline points="20 6 9 17 4 12" />
                    ) : (
                      <circle cx="12" cy="12" r="10" />
                    )}
                  </svg>
                  Las contraseñas coinciden
                </li>
              </ul>
            </div>

            <div className="form-actions">
              {!isFirstTime && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  Cancelar
                </Button>
              )}
              <Button
                type="submit"
                variant="primary"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Guardando...' : 'Guardar Contraseña'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default ChangePasswordModal;
